/**
 * Pure procedural dungeon generator.
 * Adapted from https://github.com/majidmanzarpour/threejs-procedural-dungeon (MIT)
 * Pipeline: scatter → separate → Delaunay → MST+loops → semantics → carve →
 *           decorate (table-driven props) → populate (enemies + formations).
 * No Three.js dependency: props, colliders and enemy placement are decided as
 * data here and instantiated by the game layer (issue #44).
 */

import { PLACEABLE_PROPS, DUNGEON_PROPS } from '../../config/DungeonProps.js';
import {
  ROOM_WEIGHTS,
  DIFFICULTY_UNLOCKS,
  ARCHETYPE_ROLE,
  VARIETY,
  FORMATIONS,
  POPULATION_DEFAULTS
} from '../../config/DungeonPopulation.js';

/** Relative share each room type pulls when distributing the level enemy total. */
const ROOM_TYPE_SHARE = { combat: 1, elite: 1.4, boss: 1.2, treasure: 0.4, shrine: 0.4 };

function mulberry32(seed){
  let a = seed >>> 0;
  return function(){
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeRng(seed){
  const r = mulberry32(seed);
  return {
    f:(a,b)=> a + r()*(b-a),
    i:(a,b)=> a + Math.floor(r()*(b-a+1)),
    pick:(arr)=> arr[Math.floor(r()*arr.length)],
    chance:(p)=> r() < p,
    raw:r,
    gauss(mu,sig){ let u=0,v=0; while(u===0)u=r(); while(v===0)v=r();
      return mu + sig*Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); }
  };
}

/* ---------------- constants ---------------- */
const VOID=0, FLOOR=1, WALL=2, POOL=3;
const TYPE = { ENTRANCE:'entrance', COMBAT:'combat', ELITE:'elite', TREASURE:'treasure', SHRINE:'shrine', BOSS:'boss' };
const TINT = { entrance:0x3fd0bb, combat:0x8f95a3, elite:0x9b6cf0, treasure:0xd9a441, shrine:0x5a8fe8, boss:0xd8433a };

/* ---------------- theme specs ----------------
   Each theme is one data object: palette, lighting rig, liquid shader
   params, particle system, prop-kit flags consumed by the generator,
   and name-generator word pools. Everything downstream is data-driven. */
const THEMES = {
  derelict: {
    label:'DERELICT', accent:'#ffb347',
    bg:0x06070c, fog:0x06070c, fogD:0.0021,
    hemi:[0x2e3a52, 0x0a0b10, 0.55], dir:[0xdfe8ff, 0.85],
    floor:0x7e8694, corridor:0x646c7a, wall:0x545c6a, cap:0x6e7684,
    pillar:0x626a78, debris:[0x4a505c, 0x605a4c],
    flame:0xffb347, flameCore:0xfff3c8, torchLight:[0xffa03a, 1.5, 9.5],
    cloth:0x7d5a26,
    pools:null, particles:{kind:0, color:0xaab4cc, n:110},
    nameA:['Derelict','Abandoned','Silent','Powered-Down','Vacant','Forsaken','Dark','Sealed'],
    nameB:['Deck','Quarters','Corridors','Cargo Hold','Section','Crew Module','Gallery','Bulkheads']
  },
  reactor: {
    label:'REACTOR', accent:'#ff8642',
    bg:0x0c0605, fog:0x1a0b04, fogD:0.0028,
    hemi:[0x6b3419, 0x160503, 0.55], dir:[0xffd9b0, 0.5],
    floor:0x6f6a64, corridor:0x59534c, wall:0x48423c, cap:0x615a50,
    pillar:0x564f46, debris:[0x453c34, 0x5c4a34],
    flame:0xff8c26, flameCore:0xffe9b0, torchLight:[0xff7326, 1.7, 10],
    cloth:0x7d2416,
    pools:{mode:0, colA:0x2b0d05, colB:0xff5a1f, glow:1.55, amount:0.16, pits:2},
    particles:{kind:1, color:0xffa050, n:240},
    nameA:['Overloaded','Critical','Unstable','Radiant','Venting','Scorched','Burning','Runaway'],
    nameB:['Reactor Core','Engine Rooms','Plasma Vents','Converters','Fuel Cells','Turbine Hall','Manifold','Coolant Decks']
  },
  cryo: {
    label:'CRYO', accent:'#7fd4ff',
    bg:0x060a12, fog:0x0b1522, fogD:0.0024,
    hemi:[0x3a5a80, 0x0a0e18, 0.5], dir:[0xcfe4ff, 0.82],
    floor:0x93a0b2, corridor:0x78848f, wall:0x60708a, cap:0x8194ac,
    pillar:0x70809a, debris:[0x55617a, 0x6d7a90],
    flame:0x86d9ff, flameCore:0xe8f7ff, torchLight:[0x6fc4ff, 1.35, 9.5],
    cloth:0x2b4d70,
    pools:{mode:1, colA:0x4a86c0, colB:0xbfe4ff, glow:0.55, amount:0},
    lakes:true, icicles:true, particles:{kind:2, color:0xdff0ff, n:260},
    nameA:['Frozen','Cryogenic','Subzero','Dormant','Hibernating','Icebound','Deep-Freeze','Silent'],
    nameB:['Cryo Bay','Stasis Vaults','Cold Storage','Freezer Decks','Suspension Wing','Cryostacks','Preservation Hall','Ice Lockers']
  },
  quarantine: {
    label:'QUARANTINE', accent:'#9fe66a',
    bg:0x070a07, fog:0x0a130a, fogD:0.0030,
    hemi:[0x2c4030, 0x070a06, 0.52], dir:[0xbfd8b0, 0.45],
    floor:0x7c8276, corridor:0x62685c, wall:0x4f5549, cap:0x666c5e,
    pillar:0x5c6254, debris:[0x4a4f44, 0x5e5c48],
    flame:0x8fe05a, flameCore:0xe9ffd0, torchLight:[0x77d94a, 1.35, 9],
    cloth:0x33461f,
    pools:{mode:3, colA:0x0a1207, colB:0x41602c, glow:0.6, amount:0.05, pits:1},
    graveyards:true, bones:true, particles:{kind:3, color:0x9fe66a, n:150},
    nameA:['Sealed','Infected','Contaminated','Overrun','Toxic','Festering','Breached','Condemned'],
    nameB:['Quarantine','Containment','Bio-Wing','Isolation Ward','Decon Halls','Specimen Vaults','Hazard Zone','Cell Block']
  },
  hydroponics: {
    label:'HYDROPONICS', accent:'#59d68f',
    bg:0x060c09, fog:0x091510, fogD:0.0023,
    hemi:[0x2f5a46, 0x08120c, 0.6], dir:[0xd8f0c8, 0.8],
    floor:0x848e7e, corridor:0x6a7560, wall:0x556050, cap:0x6e7a66,
    pillar:0x606c5c, debris:[0x49543f, 0x5c644c],
    flame:0x62e0a8, flameCore:0xe6fff0, torchLight:[0x4ad98e, 1.3, 9],
    cloth:0x1f5038,
    pools:{mode:2, colA:0x0c3532, colB:0x2fa38a, glow:0.6, amount:0.05, pits:1},
    roots:true, shafts:true, planters:true, particles:{kind:4, color:0x8fe6b8, n:200},
    nameA:['Overgrown','Verdant','Blooming','Tangled','Feral','Sporebound','Waking','Flourishing'],
    nameB:['Hydroponics','Greenhouse','Grow Decks','Botany Wing','Crop Vaults','Seed Banks','Gardens','Nursery']
  },
  hangar: {
    label:'HANGAR', accent:'#ffc94a',
    bg:0x05070b, fog:0x070a10, fogD:0.0019,
    hemi:[0x33405a, 0x090b12, 0.55], dir:[0xf2ecd8, 0.8],
    floor:0x767e8a, corridor:0x5e6672, wall:0x4c545e, cap:0x656d7a,
    pillar:0x5a6270, debris:[0x474e5a, 0x5e5540],
    flame:0xffd24a, flameCore:0xfff6d8, torchLight:[0xffc23a, 1.45, 10],
    cloth:0x8a6a1e,
    pools:null, cargo:true, particles:{kind:0, color:0x9fb0c8, n:120},
    nameA:['Abandoned','Echoing','Depressurized','Auxiliary','Wrecked','Scrambled','Empty','Locked'],
    nameB:['Hangar','Launch Bay','Docking Bay','Shuttle Deck','Flight Deck','Freight Hold','Landing Pads','Cargo Bay']
  },
  medbay: {
    label:'MED BAY', accent:'#66e0d0',
    bg:0x070b0d, fog:0x0a1214, fogD:0.0021,
    hemi:[0x3e5a5e, 0x0a1012, 0.62], dir:[0xe8fffb, 0.9],
    floor:0x9aa4ab, corridor:0x7f8990, wall:0x6a747c, cap:0x8a949c,
    pillar:0x78828a, debris:[0x5a646c, 0x6d7a80],
    flame:0x7df0e0, flameCore:0xeafffb, torchLight:[0x5ee6d2, 1.35, 9.5],
    cloth:0x1f5a52,
    pools:null, medbeds:true, particles:{kind:2, color:0xbdfff4, n:130},
    nameA:['Sterile','Silent','Overwhelmed','Emergency','Abandoned','Flickering','Locked-Down','Evacuated'],
    nameB:['Med Bay','Infirmary','Surgery Wing','Recovery Ward','Trauma Unit','Sick Bay','Clinic Decks','Triage Halls']
  },
  lab: {
    label:'LABORATORY', accent:'#8f7bff',
    bg:0x08070f, fog:0x0c0a18, fogD:0.0023,
    hemi:[0x453a6e, 0x0b0914, 0.55], dir:[0xd8d0ff, 0.8],
    floor:0x878da0, corridor:0x6d7386, wall:0x585e70, cap:0x747a8e,
    pillar:0x666c80, debris:[0x4e5468, 0x5f5a78],
    flame:0x9d8bff, flameCore:0xf0ecff, torchLight:[0x8a74ff, 1.4, 9.5],
    cloth:0x3a2e6e,
    pools:{mode:2, colA:0x140f2e, colB:0x7a5cff, glow:0.9, amount:0.04, pits:1},
    labtanks:true, particles:{kind:4, color:0xb4a6ff, n:160},
    nameA:['Classified','Experimental','Sealed','Forbidden','Prototype','Unstable','Restricted','Anomalous'],
    nameB:['Laboratory','Research Wing','Test Chambers','Specimen Lab','Observation Deck','Analysis Bay','Science Block','Cleanrooms']
  }
};
const THEME_KEYS = Object.keys(THEMES);

/* ---------------- name generator ---------------- */
function dungeonName(rng, th){
  const SECTOR=['A','B','C','D','E','F','G','K','M','R','S','V'];
  const NUM=['1','2','3','4','5','6','7','8','9','11','12','13','17','19','21','42'];
  return 'The ' + rng.pick(th.nameA) + ' ' + rng.pick(th.nameB) +
         ' \u00b7 Sector ' + rng.pick(SECTOR) + '-' + rng.pick(NUM);
}

/* ---------------- Delaunay (Bowyer–Watson) ---------------- */
function delaunay(pts){
  const n = pts.length;
  if(n < 2) return [];
  if(n === 2) return [[0,1]];
  const P = pts.map((p,i)=>({x:p.x + ((i*0.618033)%1)*1e-3, y:p.y + ((i*0.414213)%1)*1e-3, i}));
  let minX=1e18,minY=1e18,maxX=-1e18,maxY=-1e18;
  for(const p of P){ if(p.x<minX)minX=p.x; if(p.y<minY)minY=p.y; if(p.x>maxX)maxX=p.x; if(p.y>maxY)maxY=p.y; }
  const dm = Math.max(maxX-minX, maxY-minY, 1), mx=(minX+maxX)/2, my=(minY+maxY)/2;
  const s1={x:mx-30*dm,y:my-dm,i:-1}, s2={x:mx,y:my+30*dm,i:-2}, s3={x:mx+30*dm,y:my-dm,i:-3};
  const mkTri=(a,b,c)=>{
    const t=[a,b,c];
    const d=2*(a.x*(b.y-c.y)+b.x*(c.y-a.y)+c.x*(a.y-b.y));
    if(Math.abs(d)<1e-12){ t.ccx=0; t.ccy=0; t.r2=Infinity; return t; }
    const a2=a.x*a.x+a.y*a.y, b2=b.x*b.x+b.y*b.y, c2=c.x*c.x+c.y*c.y;
    t.ccx=(a2*(b.y-c.y)+b2*(c.y-a.y)+c2*(a.y-b.y))/d;
    t.ccy=(a2*(c.x-b.x)+b2*(a.x-c.x)+c2*(b.x-a.x))/d;
    t.r2=(a.x-t.ccx)*(a.x-t.ccx)+(a.y-t.ccy)*(a.y-t.ccy);
    return t;
  };
  let tris=[mkTri(s1,s2,s3)];
  for(const p of P){
    const bad=[], edges=[];
    for(const t of tris){ if((p.x-t.ccx)*(p.x-t.ccx)+(p.y-t.ccy)*(p.y-t.ccy) < t.r2) bad.push(t); }
    for(const t of bad) for(let e=0;e<3;e++) edges.push([t[e],t[(e+1)%3]]);
    const poly=[];
    for(let i=0;i<edges.length;i++){
      let shared=false;
      for(let j=0;j<edges.length;j++){ if(i===j) continue;
        const a=edges[i],b=edges[j];
        if((a[0]===b[0]&&a[1]===b[1])||(a[0]===b[1]&&a[1]===b[0])){shared=true;break;}
      }
      if(!shared) poly.push(edges[i]);
    }
    tris = tris.filter(t=>!bad.includes(t));
    for(const e of poly) tris.push(mkTri(e[0],e[1],p));
  }
  tris = tris.filter(t=>t[0].i>=0 && t[1].i>=0 && t[2].i>=0);
  const seen=new Set(), out=[];
  for(const t of tris) for(let e=0;e<3;e++){
    const a=t[e].i, b=t[(e+1)%3].i, lo=Math.min(a,b), hi=Math.max(a,b), k=lo*4096+hi;
    if(!seen.has(k)){ seen.add(k); out.push([lo,hi]); }
  }
  return out;
}

/* ---------------- generator ---------------- */
function generateDungeon(params){
  const t0 = performance.now();
  let attempt = 0, seed = params.seed >>> 0, d = null;
  while(attempt < 5){
    d = tryGenerate(seed, params);
    if(d.valid) break;
    seed = (Math.imul(seed, 9301) + 49297) >>> 0; attempt++;
  }
  d.stats.genMs = performance.now() - t0;
  d.stats.attempts = attempt + 1;
  return d;
}

function tryGenerate(seed, params){
  const rng = makeRng(seed);
  const N = params.roomCount;
  const TH = THEMES[params.themeKey];
  /* Level-lab asset tuning: per-kind kill switches, a density multiplier, and
     optional liquid-obstacle pools. Collidable kinds are stopped at put(); flat
     sweeps are filtered at return. */
  const assetTuning = params.assetTuning || {};
  const AT = {
    disabled: assetTuning.disabled || new Set(),
    densityMult: assetTuning.densityMult ?? 1,
    liquidPoolsEnabled: assetTuning.liquidPoolsEnabled === true
  };
  const densityChance = (c) => Math.max(0, Math.min(1, c * AT.densityMult));
  /* Corridor widening. Defaults reproduce the upstream generator's widths, so
     this file still works standalone when no config is threaded in. */
  const COR = {
    narrowWidth: params.corridors?.narrowWidth ?? 0,
    widenFactor: params.corridors?.widenFactor ?? 1,
    maxWidth: params.corridors?.maxWidth ?? 3
  };

  /* -- 1. scatter: station spine layout --
     A space station reads as straight service corridors with rooms hanging off
     both sides. One row of small junction rooms marches down the X axis (the
     spine — after carving, a continuous straight corridor with widened joints)
     and every other room sits in a column above or below it, so its MST edge
     runs perpendicular into the spine. */
  const rooms = [];
  const large = [];
  const spineCount = Math.max(4, Math.round(N / 3));
  const roomCount = N - spineCount;
  const spacingX = 12;
  const spineHalf = (spineCount - 1) * spacingX / 2;
  for(let i=0;i<spineCount;i++){
    const w = rng.i(3,4), h = rng.i(3,4);
    const cx = -spineHalf + i*spacingX + rng.i(-1,1);
    rooms.push({ id:rooms.length, cx, cy:rng.i(-1,1), w, h, arch:'s', shape:'rect',
      sx0:cx, sy0:0, spine:true,
      type:TYPE.COMBAT, depth:0, difficulty:0.2, degree:0 });
  }
  for(let i=0;i<roomCount;i++){
    const t = rng.raw();
    let w,h,arch;
    if(t<0.35){ arch='s'; w=rng.i(5,7);  h=rng.i(5,7); }
    else if(t<0.82){ arch='m'; w=rng.i(8,12); h=rng.i(8,12); }
    else { arch='l'; w=rng.i(13,18); h=rng.i(13,18); large.push(rooms.length); }
    const shape = rng.raw()<0.8 ? 'rect' : 'oct';
    const side = (i & 1) ? 1 : -1;                       // alternate above/below
    const cx = -spineHalf + ((i >> 1) / Math.max(1, (roomCount >> 1))) * spineCount * spacingX + rng.i(-2,2);
    const cy = side * (4 + h/2 + rng.i(0,3));
    rooms.push({ id:rooms.length, cx, cy, w, h, arch, shape,
      sx0:cx, sy0:cy,
      type:TYPE.COMBAT, depth:0, difficulty:0.2, degree:0 });
  }
  while(large.length < 2){
    const j = rng.i(0, rooms.length-1);
    if(rooms[j].arch !== 'l' && !rooms[j].spine){
      rooms[j].arch='l'; rooms[j].w=rng.i(13,18); rooms[j].h=rng.i(13,18); rooms[j].shape='rect'; large.push(j);
    }
  }

  /* -- 2. separate -- */
  const PAD = 2;
  { const CX=new Float64Array(N), CY=new Float64Array(N), HW=new Float64Array(N), HH=new Float64Array(N);
    for(let i=0;i<N;i++){ CX[i]=rooms[i].cx; CY[i]=rooms[i].cy; HW[i]=rooms[i].w/2+PAD/2; HH[i]=rooms[i].h/2+PAD/2; }
    for(let iter=0; iter<300; iter++){
      let moved = false;
      for(let i=0;i<N;i++) for(let j=i+1;j<N;j++){
        const ox = HW[i]+HW[j] - Math.abs(CX[i]-CX[j]);
        if(ox<=0) continue;
        const oy = HH[i]+HH[j] - Math.abs(CY[i]-CY[j]);
        if(oy<=0) continue;
        moved = true;
        // Spine junctions are pinned: they ARE the corridor line, so the other
        // room absorbs the whole displacement.
        const pi = rooms[i].spine ? 0 : (rooms[j].spine ? 1 : 0.5);
        if(ox < oy){ const s = CX[i] <= CX[j] ? -1 : 1; CX[i] += s*ox*pi; CX[j] -= s*ox*(1-pi); }
        else       { const s = CY[i] <= CY[j] ? -1 : 1; CY[i] += s*oy*pi; CY[j] -= s*oy*(1-pi); }
      }
      if(!moved) break;
    }
    for(let i=0;i<N;i++){ rooms[i].cx = Math.round(CX[i]); rooms[i].cy = Math.round(CY[i]); }
  }

  /* -- 3. graph: Delaunay -> MST -> loops -- */
  const centers = rooms.map(r=>({x:r.cx, y:r.cy}));
  let delEdges = delaunay(centers);
  if(delEdges.length === 0){ delEdges = []; for(let i=0;i<N-1;i++) delEdges.push([i,i+1]); }
  const elen = e => Math.hypot(centers[e[0]].x-centers[e[1]].x, centers[e[0]].y-centers[e[1]].y);

  const adj = Array.from({length:N},()=>[]);
  delEdges.forEach((e,idx)=>{
    // Rooms prefer to plug into the spine: room-to-room edges cost extra, so
    // the MST reads as straight corridors with rooms hanging off both sides.
    const spineTouch = rooms[e[0]].spine || rooms[e[1]].spine;
    const w = elen(e) * (spineTouch ? 1 : 1.9);
    adj[e[0]].push({b:e[1],w,idx}); adj[e[1]].push({b:e[0],w,idx});
  });
  const inT = new Uint8Array(N); inT[0]=1; let inCount=1;
  const mstIdx = new Set();
  while(inCount < N){
    let best=null;
    for(let a=0;a<N;a++) if(inT[a]) for(const e of adj[a]) if(!inT[e.b] && (!best || e.w<best.w)) best=e;
    if(!best) break;
    inT[best.b]=1; inCount++; mstIdx.add(best.idx);
  }
  if(inCount < N) return { valid:false, stats:{} };

  let mstLenSum=0; for(const i of mstIdx) mstLenSum += elen(delEdges[i]);
  const mstMean = mstLenSum / Math.max(1, mstIdx.size);

  const edges = [];
  delEdges.forEach((e,idx)=>{
    if(mstIdx.has(idx)) edges.push({a:e[0], b:e[1], isLoop:false, isCritical:false});
    else if(elen(e) < mstMean*2.2 && rng.chance(params.loopChance))
      edges.push({a:e[0], b:e[1], isLoop:true, isCritical:false});
  });
  for(const e of edges){ rooms[e.a].degree++; rooms[e.b].degree++; }

  /* leaf guard: dungeons need dead ends — prune loop edges until >=3 leaves */
  if(N >= 20){
    let leafCount = 0;
    for(let i=0;i<N;i++) if(rooms[i].degree===1) leafCount++;
    while(leafCount < 3){
      let bi=-1, bs=-1;
      for(let i=0;i<edges.length;i++){
        const e=edges[i]; if(!e.isLoop) continue;
        const s=(rooms[e.a].degree===2?1:0)+(rooms[e.b].degree===2?1:0);
        const L=Math.hypot(centers[e.a].x-centers[e.b].x, centers[e.a].y-centers[e.b].y);
        const score = s*10000 + L;
        if(score>bs){ bs=score; bi=i; }
      }
      if(bi<0) break;
      const e=edges[bi];
      if(--rooms[e.a].degree===1) leafCount++;
      if(--rooms[e.b].degree===1) leafCount++;
      edges.splice(bi,1);
    }
  }

  /* -- 4. semantics before carving -- */
  const gAdj = Array.from({length:N},()=>[]);
  edges.forEach((e,i)=>{ gAdj[e.a].push({b:e.b,i}); gAdj[e.b].push({b:e.a,i}); });

  let boss = 0; for(let i=1;i<N;i++) if(rooms[i].w*rooms[i].h > rooms[boss].w*rooms[boss].h) boss = i;

  const distFrom = src => {
    const D = new Int32Array(N).fill(-1); D[src]=0; const q=[src];
    for(let h=0; h<q.length; h++){ const a=q[h]; for(const e of gAdj[a]) if(D[e.b]<0){ D[e.b]=D[a]+1; q.push(e.b); } }
    return D;
  };
  const dB = distFrom(boss);
  let entrance = -1, bestD = -1;
  for(let i=0;i<N;i++) if(i!==boss && rooms[i].degree===1 && dB[i]>bestD){ bestD=dB[i]; entrance=i; }
  if(entrance < 0){ for(let i=0;i<N;i++) if(i!==boss && dB[i]>bestD){ bestD=dB[i]; entrance=i; } }

  const dE = distFrom(entrance);
  let maxDepth = 1; for(let i=0;i<N;i++) if(dE[i]>maxDepth) maxDepth = dE[i];
  rooms.forEach((r,i)=>{ r.depth = Math.max(0,dE[i]); r.difficulty = Math.min(1, 0.15 + 0.85*(r.depth/maxDepth)); });
  rooms[entrance].type = TYPE.ENTRANCE; rooms[entrance].difficulty = 0;
  rooms[boss].type = TYPE.BOSS; rooms[boss].difficulty = 1;

  const par = new Int32Array(N).fill(-1), pe = new Int32Array(N).fill(-1);
  { const q=[entrance], vis=new Uint8Array(N); vis[entrance]=1;
    for(let h=0; h<q.length; h++){ const a=q[h];
      for(const e of gAdj[a]) if(!vis[e.b]){ vis[e.b]=1; par[e.b]=a; pe[e.b]=e.i; q.push(e.b); } } }
  const critRooms = new Set(); let critLen = 0;
  for(let c=boss; c!==-1; c=par[c]){ critRooms.add(c); if(pe[c]>=0){ edges[pe[c]].isCritical=true; critLen++; } if(c===entrance) break; }

  const leaves = [];
  for(let i=0;i<N;i++) if(i!==entrance && i!==boss && rooms[i].degree===1) leaves.push(i);
  leaves.sort((a,b)=>rooms[b].depth-rooms[a].depth);
  leaves.slice(0,4).forEach(i=>{ rooms[i].type = TYPE.TREASURE; });

  const shrineC = [];
  for(let i=0;i<N;i++){ const r=rooms[i];
    if(r.type===TYPE.COMBAT && !critRooms.has(i) && r.depth>maxDepth*0.3 && r.depth<maxDepth*0.85) shrineC.push(i); }
  for(let k=0; k<2 && shrineC.length>0; k++){
    const j = shrineC.splice(rng.i(0,shrineC.length-1),1)[0]; rooms[j].type = TYPE.SHRINE;
  }
  const eliteC = [];
  for(const i of critRooms){ const r=rooms[i];
    if(r.type===TYPE.COMBAT && r.depth>=maxDepth*0.55 && r.depth<=maxDepth*0.85) eliteC.push(i); }
  eliteC.sort((a,b)=>rooms[a].depth-rooms[b].depth);
  for(let k=0;k<Math.min(2,eliteC.length);k++) rooms[eliteC[eliteC.length-1-k]].type = TYPE.ELITE;

  /* -- 4.5 theme room mutations (generation-aware) -- */
  if(TH.lakes){
    const lc = [];
    for(let i=0;i<N;i++){ const r=rooms[i];
      if((r.type===TYPE.COMBAT || r.type===TYPE.ELITE) && Math.min(r.w,r.h)>=9) lc.push(i); }
    for(let k=0; k<2 && lc.length>0; k++) rooms[lc.splice(rng.i(0,lc.length-1),1)[0]].lake = true;
  }
  if(TH.graveyards){
    const gc = [];
    for(let i=0;i<N;i++){ const r=rooms[i];
      if(r.type===TYPE.COMBAT && r.shape!=='ellipse' && Math.min(r.w,r.h)>=8) gc.push(i); }
    for(let k=0; k<3 && gc.length>0; k++) rooms[gc.splice(rng.i(0,gc.length-1),1)[0]].grave = true;
  }

  /* -- 5. carve + rasterize -- */
  let minX=1e9,minY=1e9,maxX=-1e9,maxY=-1e9;
  for(const r of rooms){
    minX=Math.min(minX, r.cx - Math.ceil(r.w/2)); maxX=Math.max(maxX, r.cx + Math.ceil(r.w/2));
    minY=Math.min(minY, r.cy - Math.ceil(r.h/2)); maxY=Math.max(maxY, r.cy + Math.ceil(r.h/2));
  }
  const PADG = 5, offX = PADG - minX, offY = PADG - minY;
  const W = (maxX-minX) + PADG*2 + 1, H = (maxY-minY) + PADG*2 + 1;
  for(const r of rooms){ r.cx += offX; r.cy += offY; r.sx0 += offX; r.sy0 += offY; }

  const grid = new Uint8Array(W*H);
  const roomId = new Int16Array(W*H).fill(-1);
  const corridor = new Uint8Array(W*H);
  const idx = (x,y)=> y*W + x;
  const inB = (x,y)=> x>=0 && y>=0 && x<W && y<H;

  for(const r of rooms){
    const rx=r.w/2, ry=r.h/2, sh=r.shape, ch=Math.min(rx,ry)*0.55;
    const irx2=1/(rx*rx), iry2=1/(ry*ry);
    const y0=Math.max(0,Math.floor(r.cy-ry)), y1=Math.min(H-1,Math.ceil(r.cy+ry));
    const x0=Math.max(0,Math.floor(r.cx-rx)), x1=Math.min(W-1,Math.ceil(r.cx+rx));
    for(let y=y0;y<=y1;y++){
      const dy=y-r.cy, ady=Math.abs(dy), row=y*W;
      if(ady>ry) continue;
      for(let x=x0;x<=x1;x++){
        const dx=x-r.cx, adx=Math.abs(dx);
        if(adx>rx) continue;
        let ok=true;
        if(sh==='ellipse') ok = dx*dx*irx2 + dy*dy*iry2 <= 1.0;
        else if(sh==='oct') ok = adx<=rx-ch || ady<=ry-ch || (adx-(rx-ch))+(ady-(ry-ch)) <= ch;
        if(ok){ const c=row+x; grid[c]=FLOOR; roomId[c]=r.id; }
      }
    }
  }

  const stamp = (x,y)=>{ if(inB(x,y) && grid[idx(x,y)]!==FLOOR){ grid[idx(x,y)]=FLOOR; corridor[idx(x,y)]=1; } };
  /* Offsets straddling the corridor axis. Kept centred so a widened corridor
     grows on both sides and its centre line stays on the room-to-room path;
     widths 1-3 reproduce the original hand-written table exactly. */
  const offs = w => { const o=[], start=-((w-1)>>1); for(let i=0;i<w;i++) o.push(start+i); return o; };
  const hLine=(x0,x1,y,w)=>{ const o=offs(w); for(let x=Math.min(x0,x1); x<=Math.max(x0,x1); x++) for(const k of o) stamp(x,y+k); };
  const vLine=(y0,y1,x,w)=>{ const o=offs(w); for(let y=Math.min(y0,y1); y<=Math.max(y0,y1); y++) for(const k of o) stamp(x+k,y); };

  for(const e of edges){
    const A=rooms[e.a], B=rooms[e.b];
    let w = e.isCritical ? 3 : 2;
    if(!e.isCritical && (rooms[e.a].type===TYPE.TREASURE || rooms[e.b].type===TYPE.TREASURE) && rng.chance(0.4)) w = 1;
    /* Widen anything too tight to fight in. Capped, so a corridor never ends up
       broader than the 3-4 tile spine rooms it links; the cap is also what
       keeps the ladder ordered once every base width has doubled. */
    if(w <= COR.narrowWidth) w = Math.min(COR.maxWidth, w * COR.widenFactor);
    const dx = Math.abs(A.cx-B.cx), dy = Math.abs(A.cy-B.cy);
    const ovX = Math.min(A.cx+A.w/2, B.cx+B.w/2) - Math.max(A.cx-A.w/2, B.cx-B.w/2);
    const ovY = Math.min(A.cy+A.h/2, B.cy+B.h/2) - Math.max(A.cy-A.h/2, B.cy-B.h/2);
    if(ovX >= w+2 && dy > 0){ const x = Math.round((Math.max(A.cx-A.w/2,B.cx-B.w/2)+Math.min(A.cx+A.w/2,B.cx+B.w/2))/2); vLine(A.cy,B.cy,x,w); }
    else if(ovY >= w+2 && dx > 0){ const y = Math.round((Math.max(A.cy-A.h/2,B.cy-B.h/2)+Math.min(A.cy+A.h/2,B.cy+B.h/2))/2); hLine(A.cx,B.cx,y,w); }
    else if(rng.chance(0.5)){ hLine(A.cx,B.cx,A.cy,w); vLine(A.cy,B.cy,B.cx,w); }
    else { vLine(A.cy,B.cy,A.cx,w); hLine(A.cx,B.cx,B.cy,w); }
  }

  for(let y=0;y<H;y++){
    const row=y*W;
    for(let x=0;x<W;x++){
      if(grid[row+x]!==FLOOR) continue;
      const ya=Math.max(0,y-1), yb=Math.min(H-1,y+1);
      const xa=Math.max(0,x-1), xb=Math.min(W-1,x+1);
      for(let ny=ya;ny<=yb;ny++){
        const nrow=ny*W;
        for(let nx=xa;nx<=xb;nx++){
          const ni=nrow+nx;
          if(grid[ni]===VOID) grid[ni]=WALL;
        }
      }
    }
  }

  const doorway = new Uint8Array(W*H);
  for(let y=0;y<H;y++){
    const row=y*W;
    for(let x=0;x<W;x++){
      const c=row+x;
      if(!corridor[c]) continue;
      if((x<W-1 && roomId[c+1]>=0) || (x>0 && roomId[c-1]>=0) ||
         (y<H-1 && roomId[c+W]>=0) || (y>0 && roomId[c-W]>=0)) doorway[c]=1;
    }
  }

  /* -- 5.5 theme carving: liquid pockets, frozen lakes, arches -- */
  /* Pockets replace single WALL cells with sunken liquid slots (POOL).
     Connectivity is untouched: floor cells never change, and any VOID
     exposed behind a pocket is backfilled with WALL. */
  const pools = [];
  if(AT.liquidPoolsEnabled && TH.pools && TH.pools.amount > 0){
    const nearDoorC = (x,y,d)=>{ for(let oy=-d;oy<=d;oy++) for(let ox=-d;ox<=d;ox++){
      const nx=x+ox, ny=y+oy;
      if(nx>=0&&ny>=0&&nx<W&&ny<H && doorway[idx(nx,ny)]) return true; } return false; };
    const cand = [];
    for(let y=1;y<H-1;y++) for(let x=1;x<W-1;x++){
      const c=idx(x,y);
      if(grid[c]!==WALL || nearDoorC(x,y,2)) continue;
      let nf=0;
      if(grid[c+1]===FLOOR) nf++; if(grid[c-1]===FLOOR) nf++;
      if(grid[c+W]===FLOOR) nf++; if(grid[c-W]===FLOOR) nf++;
      if(nf===1) cand.push({x,y});
    }
    for(let i=cand.length-1;i>0;i--){ const j=rng.i(0,i); const t=cand[i]; cand[i]=cand[j]; cand[j]=t; }
    const target = Math.round(cand.length * TH.pools.amount);
    for(const s of cand){
      if(pools.length >= target) break;
      let close=false;
      for(const p of pools) if(Math.max(Math.abs(p.x-s.x),Math.abs(p.y-s.y)) < 3){ close=true; break; }
      if(close) continue;
      grid[idx(s.x,s.y)] = POOL; pools.push({x:s.x, y:s.y});
    }
    for(const p of pools)
      for(let oy=-1;oy<=1;oy++) for(let ox=-1;ox<=1;ox++){
        const nx=p.x+ox, ny=p.y+oy;
        if(nx>=0&&ny>=0&&nx<W&&ny<H && grid[idx(nx,ny)]===VOID) grid[idx(nx,ny)]=WALL;
      }
  }

  /* Interior liquid pits: single floor cells sunk into lava/water/miasma.
     Carved before BFS validation, so connectivity is still guaranteed;
     interior-only + spacing >= 4 means a room can never be split. */
  if(AT.liquidPoolsEnabled && TH.pools && TH.pools.pits){
    for(const r of rooms){
      if((r.type!==TYPE.COMBAT && r.type!==TYPE.ELITE) || r.lake || r.grave) continue;
      let n = Math.min(TH.pools.pits, Math.floor(r.w*r.h/45)+1), guard=0;
      while(n>0 && guard++<40){
        const x=rng.i(Math.floor(r.cx-r.w/2)+2, Math.ceil(r.cx+r.w/2)-2);
        const y=rng.i(Math.floor(r.cy-r.h/2)+2, Math.ceil(r.cy+r.h/2)-2);
        if(!inB(x,y)) continue;
        const c=idx(x,y);
        if(roomId[c]!==r.id || grid[c]!==FLOOR || doorway[c]) continue;
        let ok=true;
        for(let oy=-1;oy<=1 && ok;oy++) for(let ox=-1;ox<=1;ox++)
          if(grid[idx(x+ox,y+oy)]!==FLOOR){ ok=false; break; }
        if(ok) for(const p of pools) if(Math.max(Math.abs(p.x-x),Math.abs(p.y-y))<4){ ok=false; break; }
        if(!ok) continue;
        grid[c]=POOL; pools.push({x,y}); n--;
      }
    }
  }

  /* Frozen lakes: interior floor cells of lake rooms stay walkable (FLOOR
     for BFS) but are flagged so rendering swaps stone tiles for ice. */
  const lakeMask = new Uint8Array(W*H);
  const lakeCells = [];
  for(const r of rooms){
    if(!r.lake) continue;
    for(let y=Math.floor(r.cy-r.h/2)+2; y<=Math.ceil(r.cy+r.h/2)-2; y++)
      for(let x=Math.floor(r.cx-r.w/2)+2; x<=Math.ceil(r.cx+r.w/2)-2; x++){
        if(!inB(x,y)) continue;
        const c=idx(x,y);
        if(roomId[c]!==r.id || grid[c]!==FLOOR || doorway[c]) continue;
        let solid=false;
        for(let oy=-1;oy<=1 && !solid;oy++) for(let ox=-1;ox<=1;ox++)
          if(grid[idx(x+ox,y+oy)]!==FLOOR){ solid=true; break; }
        if(!solid){ lakeMask[c]=1; lakeCells.push({x,y}); }
      }
  }

  /* Doorway arches: group doorway cells into runs perpendicular to the
     corridor axis; one arch frame per run no wider than a corridor. The bound
     tracks the widest corridor the carve step can produce — pinning it to 3
     would silently drop every arch once corridors widen past that. */
  const arches = [];
  { const aseen = new Uint8Array(W*H);
    for(let y=0;y<H;y++) for(let x=0;x<W;x++){
      const c=idx(x,y);
      if(!doorway[c] || aseen[c]) continue;
      let rx=0, ry=0;
      if(x<W-1 && roomId[c+1]>=0) rx=1; else if(x>0 && roomId[c-1]>=0) rx=-1;
      else if(y<H-1 && roomId[c+W]>=0) ry=1; else ry=-1;
      const px = rx===0 ? 1 : 0, py = rx===0 ? 0 : 1;
      let x0=x, y0=y, x1=x, y1=y;
      while(inB(x0-px,y0-py) && doorway[idx(x0-px,y0-py)] && !aseen[idx(x0-px,y0-py)]){ x0-=px; y0-=py; }
      while(inB(x1+px,y1+py) && doorway[idx(x1+px,y1+py)] && !aseen[idx(x1+px,y1+py)]){ x1+=px; y1+=py; }
      let len=0;
      for(let ax=x0, ay=y0;; ax+=px, ay+=py){ aseen[idx(ax,ay)]=1; len++; if(ax===x1 && ay===y1) break; }
      if(len<=Math.max(3, COR.maxWidth)) arches.push({x:(x0+x1)/2, y:(y0+y1)/2, px, py, len});
    }
  }

  /* -- 6. BFS field + validation -- */
  const bfs = new Int16Array(W*H).fill(-1);
  const ei = idx(rooms[entrance].cx, rooms[entrance].cy);
  const total = W*H;
  let floorTotal=0; for(let i=0;i<total;i++) if(grid[i]===FLOOR) floorTotal++;
  let reach=0, maxBfs=0;
  if(grid[ei]===FLOOR){
    const q = new Int32Array(floorTotal); let qh=0, qt=0;
    q[qt++]=ei; bfs[ei]=0; reach=1;
    while(qh<qt){
      const c=q[qh++], x=c%W, b=bfs[c]+1;
      let n;
      if(x>0       && grid[n=c-1]===FLOOR && bfs[n]<0){ bfs[n]=b; q[qt++]=n; reach++; }
      if(x<W-1     && grid[n=c+1]===FLOOR && bfs[n]<0){ bfs[n]=b; q[qt++]=n; reach++; }
      if(c>=W      && grid[n=c-W]===FLOOR && bfs[n]<0){ bfs[n]=b; q[qt++]=n; reach++; }
      if(c<total-W && grid[n=c+W]===FLOOR && bfs[n]<0){ bfs[n]=b; q[qt++]=n; reach++; }
    }
    maxBfs = bfs[q[qt-1]];  /* FIFO: last enqueued cell is farthest */
  }
  const valid = reach === floorTotal && floorTotal > 0;

  /* -- 7. decoration (table-driven props, issue #44) --
     Structured props are placed by iterating the DungeonProps catalog and
     dispatching on each row's `placement`, instead of a hand-written if-chain
     per prop kind. Adding/removing/retuning a structured prop is a catalog edit,
     not a generator edit. Theme-atmosphere sweeps (moss/roots/cracks/…) keep
     their geometry-coupled placement below; their rows still live in the catalog
     so the collider/density knobs are data. */
  const props=[], spawns=[];
  const occ = new Uint8Array(W*H);
  /** Cells blocked by a collidable prop — used for connectivity + spawn avoidance. */
  const solidProp = new Uint8Array(W*H);
  const nearDoor = (x,y,d)=>{ for(let oy=-d;oy<=d;oy++) for(let ox=-d;ox<=d;ox++)
    if(inB(x+ox,y+oy) && doorway[idx(x+ox,y+oy)]) return true; return false; };
  const interior = (x,y)=>{ for(let oy=-1;oy<=1;oy++) for(let ox=-1;ox<=1;ox++)
    if(!inB(x+ox,y+oy) || grid[idx(x+ox,y+oy)]!==FLOOR) return false; return true; };
  const put = (kind,x,y,rot,scale,rid)=>{
    if(AT.disabled.has(kind)) return;
    props.push({kind,x,y,rot:rot||0,scale:scale||1,roomId:rid});
    const c=idx(x,y); occ[c]=1;
    if(DUNGEON_PROPS[kind] && DUNGEON_PROPS[kind].collider) solidProp[c]=1;
  };

  /* -- 7.0 central blocks (station spec): a big room reads as a hangar bay or
     service hall when something solid parks in the middle. The footprint is a
     grid of per-cell invisible colliders (hullBlock — clamped to exactly one
     tile each, validated by the 7.1 connectivity guard) under one visual: a
     docked ship in hangars, a service core everywhere else. */
  const centralVisual = TH.cargo ? 'ship' : 'serviceCore';
  for(const r of rooms){
    if(r.spine || r.lake || r.centralBlock) continue;
    if(r.type!==TYPE.COMBAT && r.type!==TYPE.ELITE) continue;
    if(Math.min(r.w,r.h) < 11 || !rng.chance(0.8)) continue;
    const bw = TH.cargo ? 3 : rng.i(2,3), bh = TH.cargo ? 2 : rng.i(2,3);
    const x0 = Math.round(r.cx) - (bw>>1), y0 = Math.round(r.cy) - (bh>>1);
    let ok = true;
    for(let y=y0-2; y<y0+bh+2 && ok; y++) for(let x=x0-2; x<x0+bw+2; x++){
      if(!inB(x,y)) { ok=false; break; }
      const c=idx(x,y);
      if(roomId[c]!==r.id || grid[c]!==FLOOR || doorway[c] || occ[c]) { ok=false; break; }
    }
    if(!ok) continue;
    for(let y=y0; y<y0+bh; y++) for(let x=x0; x<x0+bw; x++) put('hullBlock', x, y, 0, 1, r.id);
    props.push({ kind:centralVisual, x:x0 + (bw-1)/2, y:y0 + (bh-1)/2,
                 rot: bw>=bh ? 0 : Math.PI/2, scale:1, roomId:r.id });
    r.centralBlock = true;
  }

  const rMin = r => Math.min(r.w,r.h);
  const propRot = (mode)=> mode==='free' ? rng.f(0,6.28)
    : mode==='quarterTurns' ? rng.i(0,3)*Math.PI/2
    : mode==='halfTurns' ? (rng.chance(0.5)?0:Math.PI/2)
    : mode==='jitter' ? rng.f(-0.3,0.3)
    : 0;
  const propScale = (s)=> s ? rng.f(s.min,s.max) : 1;
  const rowFitsRoom = (row,r)=>{
    if(row.roomTypes!=='*' && !row.roomTypes.includes(r.type)) return false;
    if(row.themes!=='*' && !row.themes.includes(params.themeKey)) return false;
    if(row.roomFlag && !r[row.roomFlag]) return false;
    return true;
  };
  const canPlaceProp = (x,y,p)=>{
    if(!inB(x,y)) return false;
    const c=idx(x,y);
    if(grid[c]!==FLOOR || occ[c]) return false;
    if(p.requiresInterior && !interior(x,y)) return false;
    if(p.avoidDoorways && nearDoor(x,y,p.avoidDoorways)) return false;
    return true;
  };

  const placeRoomCenter = (r,row)=>{
    const p=row.params||{};
    if(p.minRoomSize && rMin(r)<p.minRoomSize) return;
    const c=idx(r.cx,r.cy);
    if(grid[c]!==FLOOR || occ[c]) return;
    put(row.kind, r.cx, r.cy, propRot(p.rotation), propScale(p.scale), r.id);
  };
  const placeGrid = (r,row)=>{
    const p=row.params||{};
    if(p.minRoomSize && rMin(r)<p.minRoomSize) return;
    if(p.forbidShapes && p.forbidShapes.includes(r.shape)) return;
    if(p.forbidRoomFlags && p.forbidRoomFlags.some(f=>r[f])) return;
    const modStep = p.step ? 0 : (rMin(r) >= (p.largeThreshold||14) ? (p.stepLarge||4) : (p.stepSmall||3));
    const inc = p.step || 1;
    for(let y=Math.ceil(r.cy-r.h/2)+2; y<=r.cy+r.h/2-2; y+=inc)
      for(let x=Math.ceil(r.cx-r.w/2)+2; x<=r.cx+r.w/2-2; x+=inc){
        if(modStep && (((x-r.cx)%modStep)!==0 || ((y-r.cy)%modStep)!==0)) continue;
        if(p.skipCenter && x===r.cx && y===r.cy) continue;
        if(p.skipCenterRadius && Math.abs(x-r.cx)<=p.skipCenterRadius && Math.abs(y-r.cy)<=p.skipCenterRadius) continue;
        if(!canPlaceProp(x,y,p)) continue;
        if(p.chance!==undefined && !rng.chance(densityChance(p.chance))) continue;
        put(row.kind, x, y, propRot(p.rotation), propScale(p.scale), r.id);
      }
  };
  const placeRing = (r,row)=>{
    const p=row.params||{};
    const rr = Math.max(p.minRadius||2.5, rMin(r)/2 - (p.radiusInset||2));
    const a0 = rng.f(0,1), count=Math.max(1, Math.round((p.count||6)*AT.densityMult));
    for(let k=0;k<count;k++){
      const a=a0 + k*Math.PI*2/count;
      const bx=Math.round(r.cx+Math.cos(a)*rr), by=Math.round(r.cy+Math.sin(a)*rr);
      if(!inB(bx,by)) continue;
      const c=idx(bx,by);
      if(grid[c]!==FLOOR || occ[c]) continue;
      if(p.avoidDoorways && nearDoor(bx,by,p.avoidDoorways)) continue;
      put(row.kind, bx, by, propRot(p.rotation), propScale(p.scale), r.id);
    }
  };
  const placeScatterRoom = (r,row)=>{
    const p=row.params||{};
    let n=Math.max(0, Math.round((p.count||1)*AT.densityMult)), guard=0;
    while(n>0 && guard++<40){
      const x=rng.i(Math.floor(r.cx-r.w/2)+1, Math.ceil(r.cx+r.w/2)-1);
      const y=rng.i(Math.floor(r.cy-r.h/2)+1, Math.ceil(r.cy+r.h/2)-1);
      if(!inB(x,y)) continue;
      const c=idx(x,y);
      if(roomId[c]!==r.id || grid[c]!==FLOOR || occ[c]) continue;
      put(row.kind, x, y, propRot(p.rotation), propScale(p.scale), r.id);
      n--;
    }
  };

  for(const r of rooms){
    for(const row of PLACEABLE_PROPS){
      if(row.placement==='globalScatter') continue;   // ambient debris — global pass below
      // Spine junctions ARE the corridor: anything solid parked in one can
      // seal off half the station, so they only ever take flat dressing.
      if(r.spine && row.collider) continue;
      if(!rowFitsRoom(row,r)) continue;
      if(row.placement==='roomCenter') placeRoomCenter(r,row);
      else if(row.placement==='grid') placeGrid(r,row);
      else if(row.placement==='ring') placeRing(r,row);
      else if(row.placement==='scatterRoom') placeScatterRoom(r,row);
    }
  }

  /* -- 7.1 connectivity guard: solid props must not seal off a room --
     Props are interior-only and doorway-avoiding, so this is a safety net rather
     than a common path: any solid prop that leaves a room unreachable from the
     entrance (BFS treating solid props as walls) has its collider stripped. */
  const roomAnchor = (r)=>{
    for(let y=Math.floor(r.cy-r.h/2); y<=Math.ceil(r.cy+r.h/2); y++)
      for(let x=Math.floor(r.cx-r.w/2); x<=Math.ceil(r.cx+r.w/2); x++){
        if(!inB(x,y)) continue; const c=idx(x,y);
        if(roomId[c]===r.id && grid[c]===FLOOR && !solidProp[c]) return c;
      }
    return -1;
  };
  const unreachableRooms = ()=>{
    const start = idx(rooms[entrance].cx, rooms[entrance].cy);
    if(grid[start]!==FLOOR) return [];
    const seen = new Uint8Array(W*H); const q=[start]; seen[start]=1;
    for(let h=0;h<q.length;h++){
      const c=q[h], x=c%W;
      if(x>0     && grid[c-1]===FLOOR && !solidProp[c-1] && !seen[c-1]){ seen[c-1]=1; q.push(c-1); }
      if(x<W-1   && grid[c+1]===FLOOR && !solidProp[c+1] && !seen[c+1]){ seen[c+1]=1; q.push(c+1); }
      if(c>=W    && grid[c-W]===FLOOR && !solidProp[c-W] && !seen[c-W]){ seen[c-W]=1; q.push(c-W); }
      if(c<W*H-W && grid[c+W]===FLOOR && !solidProp[c+W] && !seen[c+W]){ seen[c+W]=1; q.push(c+W); }
    }
    const bad=[];
    for(const r of rooms){ const a=roomAnchor(r); if(a>=0 && !seen[a]) bad.push(r); }
    return bad;
  };
  let cGuard=0;
  while(cGuard++<8){
    const bad = unreachableRooms();
    if(bad.length===0) break;
    let removed=0;
    for(const r of bad) for(const pr of props){
      if(pr.roomId!==r.id || pr.noCollider) continue;
      if(!(DUNGEON_PROPS[pr.kind] && DUNGEON_PROPS[pr.kind].collider)) continue;
      solidProp[idx(pr.x,pr.y)]=0; pr.noCollider=true; removed++;
    }
    if(removed===0) break;
  }

  /* -- 7.2 population: enemies, archetypes and formations (issue #44) --
     Deterministic from the same seeded RNG; the game layer instantiates these. */
  const popCfg = { ...POPULATION_DEFAULTS, ...(params.population || {}) };
  const popTileSize = popCfg.tileSize ?? 2;
  const levelScale = popCfg.levelScale ?? 1;
  const safeR = popCfg.safeRadiusTiles ?? ((popCfg.safeRadiusFromEntrance ?? 0) / popTileSize);
  const exitR = popCfg.exitRadiusTiles ?? 2;
  const entX = rooms[entrance].cx, entY = rooms[entrance].cy;
  const bossX = rooms[boss].cx, bossY = rooms[boss].cy;
  const safeR2 = safeR*safeR, exitR2 = exitR*exitR;
  let enemyTarget = 0;
  const formationsUsed = new Set();

  const validSpawnCell = (x,y,r)=>{
    if(!inB(x,y)) return false;
    const c=idx(x,y);
    if(roomId[c]!==r.id) return false;
    if(grid[c]!==FLOOR) return false;              // excludes POOL / WALL / VOID
    if(doorway[c]||lakeMask[c]||occ[c]||solidProp[c]) return false;
    const dex=x-entX, dey=y-entY; if(dex*dex+dey*dey < safeR2) return false;
    const dbx=x-bossX, dby=y-bossY; if(dbx*dbx+dby*dby < exitR2) return false;
    return true;
  };
  const roomFloorArea = (r)=>{ let a=0;
    for(let y=Math.floor(r.cy-r.h/2); y<=Math.ceil(r.cy+r.h/2); y++)
      for(let x=Math.floor(r.cx-r.w/2); x<=Math.ceil(r.cx+r.w/2); x++)
        if(inB(x,y) && roomId[idx(x,y)]===r.id && grid[idx(x,y)]===FLOOR) a++;
    return a; };
  const shuffle = (arr)=>{ for(let i=arr.length-1;i>0;i--){ const j=rng.i(0,i); const t=arr[i]; arr[i]=arr[j]; arr[j]=t; } return arr; };
  const roomInteriorCells = (r)=>{ const cells=[];
    for(let y=Math.floor(r.cy-r.h/2)+1; y<=Math.ceil(r.cy+r.h/2)-1; y++)
      for(let x=Math.floor(r.cx-r.w/2)+1; x<=Math.ceil(r.cx+r.w/2)-1; x++){
        if(!inB(x,y)) continue; const c=idx(x,y);
        if(roomId[c]===r.id && grid[c]===FLOOR) cells.push({x,y});
      } return cells; };
  const scatterCells = (r)=> shuffle(roomInteriorCells(r));
  const randInterior = (r)=>{ const cells=roomInteriorCells(r); return cells.length ? cells[rng.i(0,cells.length-1)] : null; };

  /** Realises a chosen formation as an ordered list of candidate tiles. */
  const arrange = (formation, r, n)=>{
    if(formation.id==='ring'){
      const rr = Math.max(1.5, rMin(r)/2 - 1.5), a0=rng.f(0,6.28), m=Math.max(n,6), out=[];
      for(let k=0;k<m;k++){ const a=a0+k*Math.PI*2/m;
        out.push({x:Math.round(r.cx+Math.cos(a)*rr), y:Math.round(r.cy+Math.sin(a)*rr)}); }
      return out;
    }
    if(formation.id==='cluster'){
      const anchor = randInterior(r) || {x:r.cx,y:r.cy}, out=[];
      for(let rad=0; rad<=4 && out.length<n*4; rad++)
        for(let dy=-rad;dy<=rad;dy++) for(let dx=-rad;dx<=rad;dx++){
          if(Math.max(Math.abs(dx),Math.abs(dy))!==rad) continue;
          out.push({x:anchor.x+dx, y:anchor.y+dy}); }
      return out;
    }
    if(formation.id==='line'){
      const horizontal = r.w>=r.h, out=[];
      const half = Math.floor((horizontal?r.w:r.h)/2)-2, back = rng.chance(0.5)?1:-1;
      for(let d=-half; d<=half; d++) out.push(horizontal?{x:r.cx+d,y:r.cy,_rank:0}:{x:r.cx,y:r.cy+d,_rank:0});
      for(let d=-half; d<=half; d++) out.push(horizontal?{x:r.cx+d,y:r.cy+back,_rank:1}:{x:r.cx+back,y:r.cy+d,_rank:1});
      return out;
    }
    if(formation.id==='guard'){
      const out=[];
      for(let y=Math.floor(r.cy-r.h/2)-1; y<=Math.ceil(r.cy+r.h/2)+1; y++)
        for(let x=Math.floor(r.cx-r.w/2)-1; x<=Math.ceil(r.cx+r.w/2)+1; x++){
          if(!inB(x,y) || !doorway[idx(x,y)]) continue;
          const towardX = Math.abs(r.cx-x) > Math.abs(r.cy-y);
          out.push({x:x+(towardX?Math.sign(r.cx-x):0), y:y+(towardX?0:Math.sign(r.cy-y))});
        }
      return out.concat(scatterCells(r));
    }
    if(formation.id==='ambush'){
      const dx=Math.sign(entX-r.cx), dy=Math.sign(entY-r.cy), out=[];
      for(const pr of props){
        if(pr.roomId!==r.id || pr.kind!=='pillar') continue;
        out.push({x:pr.x-dx, y:pr.y-dy}); out.push({x:pr.x-dx*2, y:pr.y-dy*2});
      }
      return out.concat(scatterCells(r));
    }
    return scatterCells(r);   // 'scatter' and any fallback
  };

  const pickFormation = (r,n)=>{
    const cands = FORMATIONS.filter(f =>
      (f.roomTypes==='*' || f.roomTypes.includes(r.type)) &&
      rMin(r) >= f.minRoomSize && n >= f.minMembers && n <= f.maxMembers);
    if(cands.length===0) return FORMATIONS[0];
    let tot=0; for(const f of cands) tot+=f.weight;
    let roll=rng.raw()*tot;
    for(const f of cands){ roll-=f.weight; if(roll<=0) return f; }
    return cands[cands.length-1];
  };

  const pickWeightedRng = (keys, wfn)=>{
    let tot=0; for(const k of keys) tot+=Math.max(0,wfn(k));
    if(tot<=0) return keys[rng.i(0,keys.length-1)];
    let roll=rng.raw()*tot;
    for(const k of keys){ roll-=Math.max(0,wfn(k)); if(roll<=0) return k; }
    return keys[keys.length-1];
  };
  const pickArchetypes = (r,n,prevTypes)=>{
    const rt = ROOM_WEIGHTS[r.type] || ROOM_WEIGHTS.combat;
    let avail = Object.keys(rt).filter(a => r.difficulty >= (DIFFICULTY_UNLOCKS[a] ?? 0));
    if(avail.length===0) avail = ['grunt'];
    const cap = Math.max(1, Math.floor(VARIETY.maxSameTypeRatio * n));
    const counts = {}, result=[];
    for(let i=0;i<n;i++){
      let cand = avail.filter(a => (counts[a]||0) < cap);
      if(cand.length===0) cand = avail;
      const key = pickWeightedRng(cand, a => (rt[a]||1) * (prevTypes.has(a)?VARIETY.recentPenalty:1));
      result.push(key); counts[key]=(counts[key]||0)+1;
    }
    // Guarantee minimum variety: swap surplus duplicates for unused archetypes.
    const distinctTarget = Math.min(VARIETY.minDistinctTypes, avail.length, n);
    let distinct = new Set(result).size;
    if(distinct < distinctTarget){
      const used = new Set(result);
      const unused = avail.filter(a=>!used.has(a));
      for(let i=result.length-1; i>=0 && distinct<distinctTarget && unused.length; i--){
        const t=result[i];
        if(counts[t]>1){ const rep=unused.shift(); counts[t]--; result[i]=rep; counts[rep]=1; distinct++; }
      }
    }
    return result;
  };

  if(popCfg.enabled){
    const gmin = Math.max(0, Math.round((popCfg.minEnemies ?? 0) * levelScale));
    const gmaxRaw = Math.round((popCfg.maxEnemies ?? 0) * levelScale);
    const gmax = Math.max(gmin, Math.min(popCfg.maxPrePlaced ?? gmaxRaw, gmaxRaw));
    enemyTarget = gmax>0 ? rng.i(gmin, gmax) : 0;

    const perRoom = popCfg.perRoom || {};
    const populatable = rooms.filter(r =>
      ROOM_TYPE_SHARE[r.type] !== undefined && (!perRoom[r.type] || perRoom[r.type].max>0));
    let wsum=0;
    for(const r of populatable){ r._popW = (0.35 + r.difficulty) * ROOM_TYPE_SHARE[r.type]; wsum += r._popW; }

    // Largest-remainder split of the level total, then clamp per room.
    const frac=[]; let base=0;
    for(const r of populatable){
      const exact = wsum>0 ? enemyTarget*r._popW/wsum : 0;
      r._target = Math.floor(exact); frac.push({r, f:exact - r._target}); base += r._target;
    }
    frac.sort((a,b)=> b.f - a.f);
    let rem = enemyTarget - base;
    for(let i=0;i<frac.length && rem>0;i++){ frac[i].r._target++; rem--; }
    for(const r of populatable){
      const rb = perRoom[r.type];
      const cap = rb ? Math.max(rb.max, Math.round(rb.max*levelScale)) : 6;
      const areaCap = Math.floor(roomFloorArea(r)/6);
      r._target = Math.max(0, Math.min(r._target, cap, areaCap));
      if(rb && rb.min>0 && r._target===0 && areaCap>=rb.min) r._target = rb.min;
    }

    let prevTypes = new Set();
    for(const r of populatable){
      const n = r._target;
      if(n<=0) continue;
      const formation = pickFormation(r,n);
      const claimed=[];
      const claim = (cell)=>{ if(claimed.length<n && validSpawnCell(cell.x,cell.y,r)){
        occ[idx(cell.x,cell.y)]=1; claimed.push(cell); } };
      for(const cell of arrange(formation,r,n)){ if(claimed.length>=n) break; claim(cell); }
      if(claimed.length<n) for(const cell of scatterCells(r)){ if(claimed.length>=n) break; claim(cell); }
      if(claimed.length===0) continue;

      const archetypes = pickArchetypes(r, claimed.length, prevTypes);
      if(formation.id==='line'){
        claimed.sort((a,b)=> (b._rank||0)-(a._rank||0));     // back rank first
        archetypes.sort((a,b)=> (ARCHETYPE_ROLE[b]==='ranged'?1:0)-(ARCHETYPE_ROLE[a]==='ranged'?1:0));
      }
      const tier = r.type===TYPE.ELITE ? 3 : Math.max(1, Math.ceil(r.difficulty*3));
      for(let i=0;i<claimed.length;i++){
        const cell=claimed[i];
        spawns.push({
          x:cell.x, y:cell.y, roomId:r.id, tier,
          archetype:archetypes[i], formationId:formation.id,
          facing: Math.atan2(r.cx-cell.x, r.cy-cell.y)
        });
      }
      formationsUsed.add(formation.id);
      prevTypes = new Set(archetypes);
    }
  }
  const torchCand=[];
  for(let y=0;y<H;y++){
    const row=y*W;
    for(let x=0;x<W;x++){
      const c=row+x;
      if(grid[c]!==WALL) continue;
      if(x<W-1 && grid[c+1]===FLOOR)      torchCand.push({x,y,dx:1,dy:0});
      else if(x>0 && grid[c-1]===FLOOR)   torchCand.push({x,y,dx:-1,dy:0});
      else if(y<H-1 && grid[c+W]===FLOOR) torchCand.push({x,y,dx:0,dy:1});
      else if(y>0 && grid[c-W]===FLOOR)   torchCand.push({x,y,dx:0,dy:-1});
    }
  }
  for(let i=torchCand.length-1;i>0;i--){ const j=rng.i(0,i); const t=torchCand[i]; torchCand[i]=torchCand[j]; torchCand[j]=t; }
  const torches=[];
  for(const c of torchCand){
    let ok=true;
    for(const t of torches) if(Math.max(Math.abs(t.x-c.x),Math.abs(t.y-c.y))<4){ ok=false; break; }
    if(ok) torches.push(c);
  }
  for(let y=0;y<H;y++){
    const row=y*W;
    for(let x=0;x<W;x++){
      const c=row+x;
      if(grid[c]!==FLOOR || occ[c] || doorway[c] || lakeMask[c]) continue;
      const rid = roomId[c];
      const diff = rid>=0 ? rooms[rid].difficulty : 0.5;
      const dbz = DUNGEON_PROPS.debris.params;   // density knob lives in the catalog
      let p = params.decorDensity * dbz.baseDensity * (1.25 - 0.6*diff);
      if(corridor[c]) p *= dbz.corridorFactor;
      if(rng.chance(p)) props.push({kind:'debris',x,y,rot:rng.f(0,6.28),scale:rng.f(0.6,1.35),roomId:rid,v:rng.i(0,dbz.variants-1)});
    }
  }

  /* -- 7.5 theme prop sweeps -- */
  const floorDir = (x,y)=>{
    const c=idx(x,y);
    if(x<W-1 && grid[c+1]===FLOOR) return [1,0];
    if(x>0 && grid[c-1]===FLOOR) return [-1,0];
    if(y<H-1 && grid[c+W]===FLOOR) return [0,1];
    if(y>0 && grid[c-W]===FLOOR) return [0,-1];
    return null;
  };
  if(TH.icicles){
    for(let y=0;y<H;y++) for(let x=0;x<W;x++){
      if(grid[idx(x,y)]!==WALL) continue;
      const d = floorDir(x,y);
      if(d && rng.chance(0.06 + 0.08*params.decorDensity))
        props.push({kind:'icicle',x,y,dx:d[0],dy:d[1],rot:rng.f(0,6.28),scale:rng.f(0.7,1.3)});
    }
    for(const lc of lakeCells)
      if(rng.chance(0.05)) props.push({kind:'shardIce',x:lc.x,y:lc.y,rot:rng.f(0,6.28),scale:rng.f(0.6,1.2)});
  }
  if(TH.roots){
    const sites=[];
    for(let y=1;y<H-1;y++) for(let x=1;x<W-1;x++){
      if(grid[idx(x,y)]!==WALL) continue;
      const d = floorDir(x,y);
      if(d && roomId[idx(x+d[0],y+d[1])]>=0) sites.push({x,y,dx:d[0],dy:d[1]});
    }
    for(let i=sites.length-1;i>0;i--){ const j=rng.i(0,i); const t=sites[i]; sites[i]=sites[j]; sites[j]=t; }
    const breaches=[];
    for(const s of sites){
      if(breaches.length>=5) break;
      let close=false;
      for(const b of breaches) if(Math.max(Math.abs(b.x-s.x),Math.abs(b.y-s.y))<7){ close=true; break; }
      if(!close) breaches.push(s);
    }
    const mossMask = new Uint8Array(W*H);
    for(const b of breaches){
      props.push({kind:'roots',x:b.x,y:b.y,dx:b.dx,dy:b.dy,rot:0,scale:rng.f(0.9,1.2)});
      for(let oy=-2;oy<=2;oy++) for(let ox=-2;ox<=2;ox++){
        const nx=b.x+ox, ny=b.y+oy;
        if(!inB(nx,ny)) continue;
        const c=idx(nx,ny);
        if(grid[c]===FLOOR && !mossMask[c] && rng.chance(0.75)){
          mossMask[c]=1; props.push({kind:'moss',x:nx,y:ny,rot:rng.f(0,6.28),scale:rng.f(0.7,1.4)});
        }
      }
    }
    for(let y=0;y<H;y++) for(let x=0;x<W;x++){
      const c=idx(x,y);
      if(grid[c]!==FLOOR || mossMask[c] || lakeMask[c]) continue;
      let nw=0;
      if(x<W-1 && grid[c+1]===WALL) nw++; if(x>0 && grid[c-1]===WALL) nw++;
      if(y<H-1 && grid[c+W]===WALL) nw++; if(y>0 && grid[c-W]===WALL) nw++;
      if(nw>0 && rng.chance(0.12*params.decorDensity)){
        mossMask[c]=1; props.push({kind:'moss',x,y,rot:rng.f(0,6.28),scale:rng.f(0.6,1.3)});
      }
    }
  }
  if(TH.bones){
    for(let y=0;y<H;y++) for(let x=0;x<W;x++){
      const c=idx(x,y);
      if(grid[c]!==FLOOR || occ[c] || doorway[c] || corridor[c]) continue;
      const rid = roomId[c];
      if(rid>=0 && rooms[rid].depth>1 && rng.chance(0.018 + 0.02*params.decorDensity))
        props.push({kind:'bones',x,y,rot:rng.f(0,6.28),scale:rng.f(0.8,1.2),roomId:rid});
    }
  }
  /* liquid veins: crack decals anchored to pool edges so they read as heat/
     rot radiating FROM the liquid into the surrounding stone, never floating
     mid-room. Frost gets pale fracture lines around lake shores. */
  { const DIRS = [[1,0],[-1,0],[0,1],[0,-1]];
    if(TH.pools && (TH.pools.mode===0 || TH.pools.mode===3)){
      const pv = TH.pools.mode===0 ? 0.8 : 0.45;
      for(const p of pools)
        for(const [dx,dy] of DIRS){
          const nx=p.x+dx, ny=p.y+dy;
          if(!inB(nx,ny) || grid[idx(nx,ny)]!==FLOOR) continue;
          if(rng.chance(pv))
            props.push({kind:'crack',x:nx,y:ny,dx,dy,rot:rng.f(0,6.28),scale:rng.f(0.9,1.5)});
        }
    }
    if(TH.lakes){
      for(const lc of lakeCells)
        for(const [dx,dy] of DIRS){
          const nx=lc.x+dx, ny=lc.y+dy;
          if(!inB(nx,ny)) continue;
          const c2 = idx(nx,ny);
          if(grid[c2]!==FLOOR || lakeMask[c2]) continue;
          if(rng.chance(0.3))
            props.push({kind:'crack',x:nx,y:ny,dx,dy,rot:rng.f(0,6.28),scale:rng.f(0.7,1.2),ice:1});
        }
    }
  }
  for(const r of rooms){
    if(r.type!==TYPE.ELITE && r.type!==TYPE.BOSS) continue;
    const cand=[];
    for(let y=Math.floor(r.cy-r.h/2)-1; y<=Math.ceil(r.cy+r.h/2)+1; y++)
      for(let x=Math.floor(r.cx-r.w/2)-1; x<=Math.ceil(r.cx+r.w/2)+1; x++){
        if(!inB(x,y) || grid[idx(x,y)]!==WALL) continue;
        const d = floorDir(x,y);
        if(d && roomId[idx(x+d[0],y+d[1])]===r.id) cand.push({x,y,dx:d[0],dy:d[1]});
      }
    for(let i=cand.length-1;i>0;i--){ const j=rng.i(0,i); const t=cand[i]; cand[i]=cand[j]; cand[j]=t; }
    const placed=[];
    for(const s of cand){
      if(placed.length >= (r.type===TYPE.BOSS?4:2)) break;
      let close=false;
      for(const p of placed) if(Math.max(Math.abs(p.x-s.x),Math.abs(p.y-s.y))<4){ close=true; break; }
      if(!close){ placed.push(s); props.push({kind:'banner',x:s.x,y:s.y,dx:s.dx,dy:s.dy,rot:0,scale:1}); }
    }
  }

  const loops = edges.filter(e=>e.isLoop).length;
  let solidPropCount=0; for(let i=0;i<solidProp.length;i++) if(solidProp[i]) solidPropCount++;
  return {
    valid, params, seed, name:dungeonName(rng, TH),
    W,H, grid, roomId, corridor, doorway, bfs, maxBfs,
    rooms, edges, entrance, boss, maxDepth,
    props: AT.disabled.size ? props.filter(p=>!AT.disabled.has(p.kind)) : props,
    spawns, torches, pools, lakeCells, lakeMask, arches, solidProp,
    stats:{ rooms:N, edges:edges.length, loops, critLen, floorTiles:floorTotal, reach,
            enemies:spawns.length, enemyTarget, solidProps:solidPropCount,
            formations:[...formationsUsed], genMs:0, attempts:1 }
  };
}

export {
  VOID,
  FLOOR,
  WALL,
  POOL,
  TYPE,
  TINT,
  THEMES,
  THEME_KEYS,
  mulberry32,
  makeRng,
  dungeonName,
  delaunay,
  generateDungeon,
  tryGenerate
};

/**
 * Resolve theme key from params (AUTO picks from seed, then rotates per floor).
 * @param {number} seed
 * @param {string} themePref 'auto' or theme key
 * @param {number} [levelIndex] 1-based dungeon floor
 */
export function resolveThemeKey(seed, themePref = 'auto', levelIndex = 1) {
  if (themePref && themePref !== 'auto' && THEMES[themePref]) return themePref;
  const floorOffset = Math.max(1, Math.floor(levelIndex)) - 1;
  return THEME_KEYS[((seed >>> 0) + floorOffset) % THEME_KEYS.length];
}
